import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import { continuesCropRun, type CropRunCenter } from '@/lib/crop/sameSubjectCropRun';
import type { KeyframeTransition } from '@/lib/transitions/types';

/**
 * One auto-framing keyframe derived from scene detection. This is THE rule for
 * framing a clip nobody has reframed: the editor preview draws it and the
 * backend export (`src/lib/deriveDetectionCropKeyframes.js`) renders it. Both
 * run the shared `detectionCropKeyframesTestVectors.json`, so a change here
 * needs the same change there.
 */
export interface DetectionCropKeyframe {
  time: number;
  endTime: number;
  x: number;
  y: number;
  face_id: string;
  /** Detection's `crop_zoom` for the framed subject; absent means 1. */
  zoom?: number;
  /**
   * Detection flagged a same-subject reframe here (`camera_move`), so the
   * crop glides into this keyframe instead of cutting. Never on the first.
   */
  cameraMove?: true;
}

/** Glide length at a detection camera move (worker `CAMERA_MOVE_DURATION_S`). */
export const AUTO_CAMERA_MOVE_DURATION_SEC = 0.5;

export const FACECAM_SAFE_CENTER_ID = '__facecam_safe_center__';

/** A shot with no face that opens on a hard cut (an insert, a graphic, a
 *  puppet): centered at zoom 1. Holding the previous shot's crop there
 *  framed whatever sat where that shot's subject was (a wall, a lamp). */
export const NO_FACE_CENTER_ID = '__no_face_center__';

/** A scene starts "at" a cut within one detection frame (10 fps). */
const CUT_TOLERANCE_SEC = 0.1;
/** Shorter face-less stretches between scenes are chunk-edge noise. */
const MIN_FACELESS_SHOT_SEC = 0.2;

interface Face {
  face_id?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  is_main_speaker?: boolean;
  is_speaking?: boolean;
}

interface Region {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Span {
  start_time: number;
  end_time: number;
}

interface DetectionScene {
  start_time?: number;
  end_time?: number;
  faces?: Face[];
  crop_zoom?: unknown;
  camera_move?: unknown;
}

interface DetectionPayload {
  scenes?: DetectionScene[];
  scene_cuts?: unknown[];
  endTime?: unknown;
  facecam_face_id?: string;
  facecam_face_ids?: string[];
  facecam_url?: string;
  facecam_region?: Partial<Region>;
  facecam_spans?: Span[];
}

const finite = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const validRegion = (value: Partial<Region> | undefined): Region | null => {
  if (
    !value
    || !finite(value.x)
    || !finite(value.y)
    || !finite(value.width)
    || !finite(value.height)
    || value.width <= 0
    || value.height <= 0
  ) return null;
  return value as Region;
};

const validFace = (face: Face): face is Required<Pick<
  Face,
  'face_id' | 'x' | 'y' | 'width' | 'height'
>> & Face => (
  typeof face?.face_id === 'string'
  && finite(face.x)
  && finite(face.y)
  && finite(face.width)
  && finite(face.height)
  && face.width > 0
  && face.height > 0
);

const overlaps = (start: number, end: number, span: Span): boolean => (
  finite(span?.start_time)
  && finite(span?.end_time)
  && span.end_time > start
  && span.start_time < end
);

const isInsideFacecam = (face: Face, region: Region): boolean => {
  if (!validFace(face)) return false;
  const centerX = face.x + face.width / 2;
  const centerY = face.y + face.height / 2;
  const padding = 0.015;
  if (
    centerX >= region.x - padding
    && centerX <= region.x + region.width + padding
    && centerY >= region.y - padding
    && centerY <= region.y + region.height + padding
  ) return true;

  const intersectionWidth = Math.max(
    0,
    Math.min(face.x + face.width, region.x + region.width) - Math.max(face.x, region.x),
  );
  const intersectionHeight = Math.max(
    0,
    Math.min(face.y + face.height, region.y + region.height) - Math.max(face.y, region.y),
  );
  return (intersectionWidth * intersectionHeight) / (face.width * face.height) >= 0.15;
};

const clampBaseCropAwayFromFacecam = (
  centerX: number,
  region: Region,
  sourceAspect: number,
  targetAspect: number,
): number => {
  // At zoom 1, `targetAspect / sourceAspect` is the widest source window the
  // portrait crop can show. Higher zooms are narrower, so this is conservative.
  const halfViewport = Math.min(0.5, targetAspect / sourceAspect / 2);
  const margin = 0.01;
  if (region.x + region.width / 2 >= 0.5) {
    return Math.min(centerX, Math.max(halfViewport, region.x - halfViewport - margin));
  }
  return Math.max(
    centerX,
    Math.min(1 - halfViewport, region.x + region.width + halfViewport + margin),
  );
};

const startsAtCut = (time: number, cuts: readonly number[]): boolean => (
  cuts.some((cut) => Math.abs(cut - time) <= CUT_TOLERANCE_SEC)
);

/**
 * The stretches the scene list leaves out because no face was seen there:
 * after a scene that ends on a cut, up to the next scene (or the detection's
 * end). A face lost without a cut is the same shot, so it is not one.
 */
const facelessShots = (payload: DetectionPayload, cuts: readonly number[]): DetectionScene[] => {
  const timed = (payload.scenes || [])
    .filter((scene) => finite(scene.start_time) && finite(scene.end_time))
    .sort((a, b) => (a.start_time as number) - (b.start_time as number));
  const shots: DetectionScene[] = [];
  let coveredUntil = -Infinity;
  timed.forEach((scene, index) => {
    coveredUntil = Math.max(coveredUntil, scene.end_time as number);
    const next = index + 1 < timed.length
      ? timed[index + 1].start_time as number
      : finite(payload.endTime) ? payload.endTime : null;
    if (
      next === null
      || next - coveredUntil < MIN_FACELESS_SHOT_SEC
      || !startsAtCut(coveredUntil, cuts)
    ) return;
    shots.push({ start_time: coveredUntil, end_time: next, faces: [] });
  });
  return shots;
};

/**
 * The face the worker measured `crop_zoom` and `camera_move` on
 * (`selectSubject` in deriveCropZoom.js / deriveCameraMoves.js). Those two
 * annotations only describe the framing when the crop follows this face.
 */
const detectorSubject = (scene: DetectionScene): Face | null => {
  const faces = Array.isArray(scene.faces) ? scene.faces : [];
  return faces.find((face) => face?.is_main_speaker)
    || faces.find((face) => face?.is_speaking)
    || faces[0]
    || null;
};

interface SceneCrop {
  time: number;
  endTime: number;
  center: CropRunCenter | null;
  facecamActive: boolean;
  cameraMove: boolean;
}

export function deriveDetectionCropKeyframes(input: {
  detection: unknown;
  isPortrait: boolean;
  sourceAspect?: number;
  targetAspect?: number;
}): DetectionCropKeyframe[] {
  const payloads = (Array.isArray(input.detection)
    ? input.detection
    : [input.detection]
  ).filter((value): value is DetectionPayload => !!value && typeof value === 'object');
  const scenes: SceneCrop[] = [];
  const sourceAspect = finite(input.sourceAspect) && input.sourceAspect > 0
    ? input.sourceAspect
    : 16 / 9;
  const targetAspect = finite(input.targetAspect) && input.targetAspect > 0
    ? input.targetAspect
    : 9 / 16;

  for (const payload of payloads) {
    const region = validRegion(payload.facecam_region);
    const spans = Array.isArray(payload.facecam_spans) ? payload.facecam_spans : null;
    const legacyFacecamAlwaysOn = !!payload.facecam_url && region && spans === null;
    const facecamFaceIds = new Set(
      [
        payload.facecam_face_id,
        ...(Array.isArray(payload.facecam_face_ids) ? payload.facecam_face_ids : []),
      ].filter((faceId): faceId is string => (
        typeof faceId === 'string' && faceId.length > 0
      )),
    );

    const cuts = (Array.isArray(payload.scene_cuts) ? payload.scene_cuts : []).filter(finite);
    for (const scene of [...(payload.scenes || []), ...facelessShots(payload, cuts)]) {
      if (!finite(scene.start_time) || !finite(scene.end_time)) continue;
      const time = scene.start_time;
      const endTime = scene.end_time;
      const facecamActive = !!(
        input.isPortrait
        && region
        && (
          legacyFacecamAlwaysOn
          || spans?.some((span) => overlaps(time, endTime, span))
        )
      );
      const allFaces = (scene.faces || []).filter(validFace);
      const faces = facecamActive && region
        ? allFaces.filter((face) => (
          !facecamFaceIds.has(face.face_id)
          && !isInsideFacecam(face, region)
        ))
        : allFaces;
      const mainSpeaker = faces.find((face) => face.is_main_speaker);
      const speakingFace = mainSpeaker || faces.find((face) => face.is_speaking);
      const fallbackFace = facecamActive
        ? [...faces].sort((a, b) => b.width * b.height - a.width * a.height)[0]
        : faces[0];
      const selected = speakingFace || fallbackFace;

      if (!selected && facecamActive && region) {
        scenes.push({
          time,
          endTime,
          center: {
            face_id: FACECAM_SAFE_CENTER_ID,
            x: clampBaseCropAwayFromFacecam(0.5, region, sourceAspect, targetAspect) * 100,
            y: 50,
          },
          facecamActive,
          cameraMove: false,
        });
        continue;
      }
      if (!selected && startsAtCut(time, cuts)) {
        scenes.push({
          time,
          endTime,
          center: { face_id: NO_FACE_CENTER_ID, x: 50, y: 50 },
          facecamActive,
          cameraMove: false,
        });
        continue;
      }
      if (!selected) {
        // Face-less scenes inherit the nearest crop center (and zoom) once
        // every scene is sorted.
        scenes.push({ time, endTime, center: null, facecamActive, cameraMove: false });
        continue;
      }

      let centerX = selected.x + selected.width / 2;
      if (facecamActive && region) {
        centerX = clampBaseCropAwayFromFacecam(
          centerX,
          region,
          sourceAspect,
          targetAspect,
        );
      }
      const describesSelected = detectorSubject(scene)?.face_id === selected.face_id;
      const zoom = describesSelected && finite(scene.crop_zoom) && scene.crop_zoom > 0
        ? scene.crop_zoom
        : undefined;
      scenes.push({
        time,
        endTime,
        center: {
          face_id: selected.face_id,
          x: centerX * 100,
          y: (selected.y + selected.height / 2) * 100,
          ...(zoom !== undefined ? { zoom } : {}),
        },
        facecamActive,
        cameraMove: describesSelected && scene.camera_move === true,
      });
    }
  }

  scenes.sort((a, b) => a.time - b.time);
  for (let i = 1; i < scenes.length; i += 1) {
    if (!scenes[i].center && scenes[i - 1].center) {
      scenes[i] = { ...scenes[i], center: scenes[i - 1].center };
    }
  }
  for (let i = scenes.length - 2; i >= 0; i -= 1) {
    if (!scenes[i].center && scenes[i + 1].center) {
      scenes[i] = { ...scenes[i], center: scenes[i + 1].center };
    }
  }

  const resolved = scenes.filter((scene) => !!scene.center);
  if (resolved.length === 0) return [];
  const runs: SceneCrop[] = [];
  let current = { ...resolved[0] };
  for (let i = 1; i < resolved.length; i += 1) {
    const next = resolved[i];
    // A run keeps its first scene's center. It ends when the subject moved,
    // changed zoom or face (continuesCropRun), at a detection camera move,
    // and where a facecam PiP span begins: only a center clamped inside the
    // span keeps the base crop's window off the inset.
    if (
      continuesCropRun(current.center, next.center)
      && !next.cameraMove
      && !(next.facecamActive && !current.facecamActive)
    ) {
      current.endTime = next.endTime;
    } else {
      runs.push(current);
      current = { ...next };
    }
  }
  runs.push(current);

  return runs.map((run, index) => {
    const center = run.center!;
    return {
      time: run.time,
      endTime: run.endTime,
      x: center.x,
      y: center.y,
      face_id: center.face_id,
      ...(center.zoom !== undefined ? { zoom: center.zoom } : {}),
      ...(index > 0 && run.cameraMove ? { cameraMove: true as const } : {}),
    };
  });
}

/**
 * The glides detection asks for: a `camera-move` keyframe transition (source
 * seconds) into each keyframe flagged `cameraMove`. The export adds the same
 * ones while the clip's framing is the detected one; once crop keyframes are
 * saved, neither side adds them.
 */
export function autoCameraMoveTransitions(
  keyframes: readonly DetectionCropKeyframe[],
): KeyframeTransition[] {
  return keyframes
    .filter((keyframe) => keyframe.cameraMove === true && keyframe.time > 0)
    .map((keyframe) => ({
      atTime: Math.round(keyframe.time * 1000) / 1000,
      kind: 'camera-move',
      durationSec: AUTO_CAMERA_MOVE_DURATION_SEC,
    }));
}

/** Detection keyframes as editor crop keyframes: position and zoom only. */
export function toCropCenterKeyframes(
  keyframes: readonly DetectionCropKeyframe[],
): CropCenterKeyframe[] {
  return keyframes.map(({ time, endTime, x, y, zoom }) => (
    zoom !== undefined ? { time, endTime, x, y, zoom } : { time, endTime, x, y }
  ));
}
